<template>
  <v-container fluid class="customer-detail pa-4 pa-md-6">
    <div v-if="!loading">
      <!-- Header -->
      <div class="d-flex align-center mb-6">
        <v-btn icon class="mr-3" @click="goBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h5 font-weight-bold grey--text text--darken-3">
            {{ customer.name || "Chi tiết khách hàng" }}
          </h1>
          <span v-if="customer.code" class="text-caption grey--text">
            Mã: {{ customer.code }}
          </span>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          depressed
          rounded
          @click="saveCustomer"
          :loading="saving"
          class="text-none px-5"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Cập nhật
        </v-btn>
      </div>

      <v-form ref="form">
        <v-row>
          <!-- Left Column -->
          <v-col cols="12" lg="7">
            <!-- Basic Info -->
            <v-card flat class="rounded-lg mb-4" style="border: 1px solid #e0e0e0">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-4">
                  <v-avatar color="primary" size="36" class="mr-3">
                    <v-icon color="white" size="20">mdi-account</v-icon>
                  </v-avatar>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
                    Thông tin cơ bản
                  </span>
                </div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="customer.name"
                      label="Tên khách hàng *"
                      :rules="[rules.required]"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="customer.code"
                      label="Mã khách hàng *"
                      :rules="[rules.required]"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="customer.email"
                      label="Email"
                      :rules="[rules.email]"
                      outlined
                      dense
                      prepend-inner-icon="mdi-email-outline"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="customer.phone_number"
                      label="Số điện thoại"
                      outlined
                      dense
                      prepend-inner-icon="mdi-phone-outline"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="customer.address"
                      label="Địa chỉ"
                      outlined
                      dense
                      prepend-inner-icon="mdi-map-marker-outline"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Notes -->
            <v-card flat class="rounded-lg mb-4" style="border: 1px solid #e0e0e0">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-4">
                  <v-avatar color="blue-grey" size="36" class="mr-3">
                    <v-icon color="white" size="20">mdi-note-text</v-icon>
                  </v-avatar>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
                    Mô tả & Ghi chú
                  </span>
                </div>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="customer.description"
                      label="Mô tả"
                      outlined
                      dense
                      rows="3"
                      auto-grow
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="customer.note"
                      label="Ghi chú"
                      outlined
                      dense
                      rows="3"
                      auto-grow
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Documents -->
            <v-card flat class="rounded-lg" style="border: 1px solid #e0e0e0">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-4">
                  <v-avatar color="deep-purple" size="36" class="mr-3">
                    <v-icon color="white" size="20">mdi-folder-multiple</v-icon>
                  </v-avatar>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
                    Hồ sơ khách hàng
                  </span>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="deep-purple"
                    depressed
                    rounded
                    small
                    class="text-none"
                    @click="addDocument"
                    dark
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Thêm hồ sơ
                  </v-btn>
                </div>

                <div
                  v-if="!customer.documents || customer.documents.length === 0"
                  class="empty-state text-center py-8"
                >
                  <v-icon size="56" color="grey lighten-1">mdi-folder-open-outline</v-icon>
                  <p class="text-body-2 grey--text mt-3 mb-0">
                    Chưa có hồ sơ nào. Nhấn "Thêm hồ sơ" để bắt đầu.
                  </p>
                </div>

                <v-expansion-panels
                  v-else
                  v-model="expandedDocument"
                  flat
                  class="document-panels"
                  accordion
                >
                  <v-expansion-panel
                    v-for="(doc, docIndex) in customer.documents"
                    :key="doc._key || doc.id"
                    class="mb-3 rounded-lg"
                  >
                    <v-expansion-panel-header class="py-3 px-4">
                      <div class="d-flex align-center" style="width: 100%">
                        <v-icon color="deep-purple" class="mr-3">mdi-folder-outline</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-3">
                            {{ doc.name || "Hồ sơ chưa đặt tên" }}
                          </div>
                          <div class="text-caption grey--text">
                            {{ totalFiles(doc) }} tệp
                          </div>
                        </div>
                        <v-btn
                          icon
                          small
                          @click.stop="removeDocument(docIndex)"
                          class="mr-2"
                        >
                          <v-icon small color="error">mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-4 pb-4">
                      <v-text-field
                        v-model="doc.name"
                        label="Tên loại hồ sơ *"
                        placeholder="VD: Hợp đồng, Giấy phép kinh doanh..."
                        outlined
                        dense
                        hide-details="auto"
                        class="mb-4"
                      ></v-text-field>

                      <!-- Uploaded files (existing paths) -->
                      <div v-if="doc.paths && doc.paths.length > 0" class="mb-3">
                        <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">
                          <v-icon x-small>mdi-check-circle</v-icon>
                          Đã tải lên
                        </div>
                        <div
                          v-for="(item, fileIndex) in doc.paths"
                          :key="`existing-${fileIndex}`"
                          class="file-row d-flex align-center py-2 px-3 rounded mb-2"
                        >
                          <div
                            class="file-thumb mr-3"
                            :class="{ clickable: isViewable(item.original_name) }"
                            @click="openPreview(item)"
                          >
                            <img
                              v-if="isImage(item.original_name)"
                              :src="resolveFileUrl(item.path)"
                              class="thumb-img"
                            />
                            <div v-else class="thumb-ext" :style="extStyle(item.original_name)">
                              {{ fileExt(item.original_name) }}
                            </div>
                          </div>
                          <div class="flex-grow-1 mr-3" style="min-width: 0">
                            <div
                              v-if="editingKey !== `exist-${docIndex}-${fileIndex}`"
                              class="d-flex align-center file-name-display"
                              @click="startEdit(`exist-${docIndex}-${fileIndex}`)"
                            >
                              <span class="text-body-2 font-weight-medium grey--text text--darken-3 text-truncate">
                                {{ item.name || item.original_name }}
                              </span>
                              <v-icon x-small class="ml-2 edit-icon" color="deep-purple">
                                mdi-pencil-outline
                              </v-icon>
                            </div>
                            <v-text-field
                              v-else
                              v-model="item.name"
                              :ref="`editInput-exist-${docIndex}-${fileIndex}`"
                              placeholder="Nhập tên hiển thị"
                              dense
                              hide-details
                              outlined
                              autofocus
                              class="file-name-input-edit"
                              @blur="stopEdit"
                              @keyup.enter="stopEdit"
                              @keyup.esc="stopEdit"
                            ></v-text-field>
                            <div class="text-caption grey--text text-truncate mt-1">
                              <span class="ext-chip" :style="extStyle(item.original_name)">
                                {{ fileExt(item.original_name) || "file" }}
                              </span>
                              <span v-if="item.size">· {{ formatFileSize(item.size) }}</span>
                            </div>
                          </div>
                          <v-btn
                            v-if="isViewable(item.original_name)"
                            icon
                            small
                            @click="openPreview(item)"
                            class="mr-1"
                          >
                            <v-icon small color="deep-purple">mdi-eye-outline</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            small
                            :href="resolveFileUrl(item.path)"
                            :download="item.original_name"
                            target="_blank"
                            class="mr-1"
                          >
                            <v-icon small color="primary">mdi-download</v-icon>
                          </v-btn>
                          <v-btn icon small @click="removeExistingFile(docIndex, fileIndex)">
                            <v-icon small color="error">mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <!-- New files pending upload -->
                      <div v-if="doc._pending && doc._pending.length > 0" class="mb-3">
                        <div class="text-caption font-weight-bold orange--text text--darken-2 mb-2">
                          <v-icon x-small color="orange darken-2">mdi-clock-outline</v-icon>
                          Chờ tải lên
                        </div>
                        <div
                          v-for="(pending, pIndex) in doc._pending"
                          :key="`pending-${pIndex}`"
                          class="file-row pending-row d-flex align-center py-2 px-3 rounded mb-2"
                        >
                          <div
                            class="file-thumb mr-3"
                            :class="{ clickable: isViewable(pending.file.name) }"
                            @click="openPendingPreview(pending)"
                          >
                            <img
                              v-if="isImage(pending.file.name)"
                              :src="pending.previewUrl"
                              class="thumb-img"
                            />
                            <div v-else class="thumb-ext" :style="extStyle(pending.file.name)">
                              {{ fileExt(pending.file.name) }}
                            </div>
                          </div>
                          <div class="flex-grow-1 mr-3" style="min-width: 0">
                            <div
                              v-if="editingKey !== `pending-${docIndex}-${pIndex}`"
                              class="d-flex align-center file-name-display"
                              @click="startEdit(`pending-${docIndex}-${pIndex}`)"
                            >
                              <span class="text-body-2 font-weight-medium grey--text text--darken-3 text-truncate">
                                {{ pending.name || pending.file.name }}
                              </span>
                              <v-icon x-small class="ml-2 edit-icon" color="deep-purple">
                                mdi-pencil-outline
                              </v-icon>
                            </div>
                            <v-text-field
                              v-else
                              v-model="pending.name"
                              placeholder="Nhập tên hiển thị"
                              dense
                              hide-details
                              outlined
                              autofocus
                              class="file-name-input-edit"
                              @blur="stopEdit"
                              @keyup.enter="stopEdit"
                              @keyup.esc="stopEdit"
                            ></v-text-field>
                            <div class="text-caption grey--text text-truncate mt-1">
                              <span class="ext-chip" :style="extStyle(pending.file.name)">
                                {{ fileExt(pending.file.name) || "file" }}
                              </span>
                              · {{ formatFileSize(pending.file.size) }}
                            </div>
                          </div>
                          <v-btn icon small @click="removePendingFile(docIndex, pIndex)">
                            <v-icon small color="error">mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <!-- Dropzone -->
                      <div
                        class="dropzone d-flex flex-column align-center justify-center pa-5"
                        :class="{ 'dropzone-active': dragOverIndex === docIndex }"
                        @dragover.prevent="dragOverIndex = docIndex"
                        @dragleave.prevent="dragOverIndex = null"
                        @drop.prevent="onDrop($event, docIndex)"
                        @click="triggerFileInput(docIndex)"
                      >
                        <v-icon size="36" color="deep-purple lighten-2">
                          mdi-cloud-upload-outline
                        </v-icon>
                        <div class="text-body-2 mt-2 grey--text text--darken-2">
                          <span class="deep-purple--text font-weight-medium">Chọn tệp</span>
                          hoặc kéo thả vào đây
                        </div>
                        <div class="text-caption grey--text mt-1">
                          Tối đa 20MB mỗi tệp
                        </div>
                        <input
                          :ref="`fileInput${docIndex}`"
                          type="file"
                          multiple
                          style="display: none"
                          @change="onFileSelected($event, docIndex)"
                        />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column -->
          <v-col cols="12" lg="5">
            <!-- Finance -->
            <v-card flat class="rounded-lg mb-4" style="border: 1px solid #e0e0e0">
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-4">
                  <v-avatar color="success" size="36" class="mr-3">
                    <v-icon color="white" size="20">mdi-cash</v-icon>
                  </v-avatar>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
                    Thông tin tài chính
                  </span>
                </div>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model.number="customer.total_debt"
                      label="Tổng nợ"
                      type="number"
                      outlined
                      dense
                      suffix="₫"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.number="customer.paid_amount"
                      label="Đã thanh toán"
                      type="number"
                      outlined
                      dense
                      suffix="₫"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <!-- Summary -->
                <div class="finance-summary rounded-lg pa-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-body-2 grey--text">Tổng nợ</span>
                    <span class="text-body-2 font-weight-medium">
                      {{ formatCurrency(customer.total_debt) }}
                    </span>
                  </div>
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-body-2 grey--text">Đã thanh toán</span>
                    <span class="text-body-2 font-weight-medium success--text">
                      {{ formatCurrency(customer.paid_amount) }}
                    </span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-subtitle-2 font-weight-bold">Còn lại</span>
                    <v-chip
                      :color="remainingDebt <= 0 ? 'success' : 'error'"
                      label
                      small
                      class="font-weight-bold"
                    >
                      {{ formatCurrency(remainingDebt) }}
                    </v-chip>
                  </div>
                  <!-- Progress bar -->
                  <v-progress-linear
                    v-if="customer.total_debt > 0"
                    :value="paymentProgress"
                    :color="remainingDebt <= 0 ? 'success' : 'primary'"
                    rounded
                    height="6"
                    class="mt-3"
                    background-color="grey lighten-3"
                  ></v-progress-linear>
                  <div v-if="customer.total_debt > 0" class="text-right mt-1">
                    <span class="text-caption grey--text">
                      {{ Math.min(paymentProgress, 100).toFixed(1) }}% đã thanh toán
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- System Info -->
            <v-card
              v-if="customer.created_at || customer.updated_at"
              flat
              class="rounded-lg"
              style="border: 1px solid #e0e0e0"
            >
              <v-card-text class="pa-5">
                <div class="d-flex align-center mb-4">
                  <v-avatar color="grey" size="36" class="mr-3">
                    <v-icon color="white" size="20">mdi-clock-outline</v-icon>
                  </v-avatar>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
                    Thông tin hệ thống
                  </span>
                </div>
                <div class="d-flex flex-column">
                  <div v-if="customer.created_at" class="d-flex align-center py-2">
                    <v-icon small color="grey" class="mr-3">mdi-calendar-plus</v-icon>
                    <div>
                      <div class="text-caption grey--text">Ngày tạo</div>
                      <div class="text-body-2">{{ formatDate(customer.created_at) }}</div>
                    </div>
                  </div>
                  <v-divider
                    v-if="customer.created_at && customer.updated_at"
                  ></v-divider>
                  <div v-if="customer.updated_at" class="d-flex align-center py-2">
                    <v-icon small color="grey" class="mr-3">mdi-calendar-edit</v-icon>
                    <div>
                      <div class="text-caption grey--text">Cập nhật lần cuối</div>
                      <div class="text-body-2">{{ formatDate(customer.updated_at) }}</div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-form>

      <!-- Image preview dialog -->
      <v-dialog v-model="previewDialog" max-width="900" overlay-opacity="0.85">
        <div class="preview-wrapper text-center" v-if="previewItem">
          <div class="preview-header d-flex align-center px-4 py-3">
            <v-icon color="white" class="mr-2">mdi-image-outline</v-icon>
            <div class="flex-grow-1 text-truncate white--text text-left">
              <div class="text-subtitle-2 font-weight-bold">
                {{ previewItem.name || previewItem.original_name }}
              </div>
              <div class="text-caption" style="opacity: 0.8">
                {{ previewItem.original_name }}
              </div>
            </div>
            <v-btn
              icon
              dark
              :href="previewItem.src"
              :download="previewItem.original_name"
              target="_blank"
              class="mr-1"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn icon dark @click="previewDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="preview-body">
            <img :src="previewItem.src" class="preview-img" />
          </div>
        </div>
      </v-dialog>

      <!-- Upload progress overlay -->
      <v-overlay :value="saving" opacity="0.7" z-index="999">
        <div class="text-center">
          <v-progress-circular
            :value="uploadProgress"
            :indeterminate="uploadProgress === 0 || uploadProgress === 100"
            size="72"
            width="5"
            color="white"
          >
            <span class="text-caption white--text" v-if="uploadProgress > 0">
              {{ uploadProgress }}%
            </span>
          </v-progress-circular>
          <p class="text-body-2 white--text mt-4">
            {{ uploadStatusText }}
          </p>
        </div>
      </v-overlay>
    </div>

    <!-- Loading -->
    <div v-else class="d-flex flex-column align-center justify-center" style="min-height: 400px">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        width="3"
      ></v-progress-circular>
      <p class="text-body-2 grey--text mt-4">Đang tải dữ liệu...</p>
    </div>
  </v-container>
</template>

<script>
import { getCustomer, updateCustomerWithDocuments } from "@/api/customer";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      uploadProgress: 0,
      expandedDocument: null,
      dragOverIndex: null,
      previewDialog: false,
      previewItem: null,
      editingKey: null,
      customer: {
        name: "",
        code: "",
        email: "",
        phone_number: "",
        address: "",
        description: "",
        note: "",
        total_debt: 0,
        paid_amount: 0,
        documents: [],
      },
      rules: {
        required: (value) => !!value || "Trường này là bắt buộc",
        email: (value) => {
          if (!value) return true;
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(value) || "Email không hợp lệ";
        },
      },
    };
  },
  computed: {
    customerId() {
      return this.$route.params.id;
    },
    remainingDebt() {
      return (this.customer.total_debt || 0) - (this.customer.paid_amount || 0);
    },
    paymentProgress() {
      if (!this.customer.total_debt) return 0;
      return ((this.customer.paid_amount || 0) / this.customer.total_debt) * 100;
    },
    uploadStatusText() {
      return this.uploadProgress > 0 && this.uploadProgress < 100
        ? "Đang tải lên..."
        : "Đang lưu...";
    },
    apiOrigin() {
      const base = process.env.VUE_APP_BASE_API || "";
      try {
        const url = new URL(base, window.location.origin);
        return `${url.protocol}//${url.host}`;
      } catch (e) {
        return window.location.origin;
      }
    },
  },
  created() {
    this.getCustomerData();
  },
  beforeDestroy() {
    (this.customer.documents || []).forEach((doc) => {
      (doc._pending || []).forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    });
  },
  methods: {
    async getCustomerData() {
      this.loading = true;
      try {
        const response = await getCustomer(this.customerId);
        this.customer = {
          ...response.data,
          documents: (response.data.documents || []).map((doc) => ({
            ...doc,
            paths: (doc.paths || []).map((p) => ({ ...p })),
            _pending: [],
            _key: `doc-${doc.id}`,
          })),
        };
      } catch (error) {
        this.$toast.error("Không thể tải thông tin khách hàng");
        this.$router.push("/khachhang");
      } finally {
        this.loading = false;
      }
    },
    async saveCustomer() {
      if (!this.$refs.form.validate()) {
        return;
      }
      const invalidDoc = (this.customer.documents || []).find(
        (doc) => !doc.name || !doc.name.trim()
      );
      if (invalidDoc) {
        this.$toast.error("Vui lòng nhập tên cho tất cả các hồ sơ");
        return;
      }

      this.saving = true;
      this.uploadProgress = 0;
      try {
        const formData = new FormData();
        formData.append("_method", "PUT");
        const fields = [
          "name",
          "code",
          "email",
          "phone_number",
          "address",
          "description",
          "note",
          "total_debt",
          "paid_amount",
        ];
        fields.forEach((field) => {
          const value = this.customer[field];
          if (value !== null && value !== undefined) {
            formData.append(field, value);
          }
        });

        (this.customer.documents || []).forEach((doc, docIndex) => {
          const prefix = `documents[${docIndex}]`;
          if (doc.id) formData.append(`${prefix}[id]`, doc.id);
          formData.append(`${prefix}[name]`, doc.name);

          (doc.paths || []).forEach((item, pathIndex) => {
            formData.append(`${prefix}[paths][${pathIndex}][path]`, item.path);
            formData.append(
              `${prefix}[paths][${pathIndex}][name]`,
              item.name || item.original_name
            );
            formData.append(
              `${prefix}[paths][${pathIndex}][original_name]`,
              item.original_name
            );
            if (item.size !== null && item.size !== undefined) {
              formData.append(`${prefix}[paths][${pathIndex}][size]`, item.size);
            }
          });

          (doc._pending || []).forEach((pending, fileIndex) => {
            formData.append(`${prefix}[files][${fileIndex}]`, pending.file);
            formData.append(
              `${prefix}[file_names][${fileIndex}]`,
              pending.name || pending.file.name
            );
          });
        });

        await updateCustomerWithDocuments(
          this.customerId,
          formData,
          (progress) => {
            this.uploadProgress = progress;
          }
        );
        this.$toast.success("Cập nhật khách hàng thành công");
        await this.getCustomerData();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.message || "Không thể cập nhật khách hàng"
        );
      } finally {
        this.saving = false;
        this.uploadProgress = 0;
      }
    },
    addDocument() {
      if (!this.customer.documents) {
        this.$set(this.customer, "documents", []);
      }
      this.customer.documents.push({
        name: "",
        paths: [],
        _pending: [],
        _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      });
      this.expandedDocument = this.customer.documents.length - 1;
    },
    removeDocument(index) {
      this.customer.documents.splice(index, 1);
    },
    removeExistingFile(docIndex, fileIndex) {
      this.customer.documents[docIndex].paths.splice(fileIndex, 1);
    },
    removePendingFile(docIndex, pIndex) {
      const pending = this.customer.documents[docIndex]._pending[pIndex];
      if (pending && pending.previewUrl) URL.revokeObjectURL(pending.previewUrl);
      this.customer.documents[docIndex]._pending.splice(pIndex, 1);
    },
    triggerFileInput(docIndex) {
      const ref = this.$refs[`fileInput${docIndex}`];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input) input.click();
    },
    onFileSelected(event, docIndex) {
      const files = Array.from(event.target.files || []);
      this.appendPendingFiles(docIndex, files);
      event.target.value = "";
    },
    onDrop(event, docIndex) {
      this.dragOverIndex = null;
      const files = Array.from(event.dataTransfer.files || []);
      this.appendPendingFiles(docIndex, files);
    },
    appendPendingFiles(docIndex, files) {
      const maxSize = 20 * 1024 * 1024;
      const doc = this.customer.documents[docIndex];
      if (!doc._pending) this.$set(doc, "_pending", []);
      files.forEach((file) => {
        if (file.size > maxSize) {
          this.$toast.error(`Tệp "${file.name}" vượt quá 20MB`);
          return;
        }
        doc._pending.push({
          file,
          name: file.name.replace(/\.[^.]+$/, ""),
          previewUrl: this.isImage(file.name) ? URL.createObjectURL(file) : null,
        });
      });
    },
    isImage(filename) {
      const ext = this.fileExt(filename);
      return ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);
    },
    isViewable(filename) {
      const ext = this.fileExt(filename);
      return [
        "png", "jpg", "jpeg", "gif", "webp", "bmp", "svg",
        "pdf", "txt", "mp4", "webm", "mp3", "wav",
      ].includes(ext);
    },
    fileExt(filename) {
      if (!filename || filename.indexOf(".") === -1) return "";
      return filename.split(".").pop().toLowerCase();
    },
    extStyle(filename) {
      const ext = this.fileExt(filename);
      const palette = {
        pdf: "#e53935",
        doc: "#1e88e5", docx: "#1e88e5",
        xls: "#43a047", xlsx: "#43a047", csv: "#43a047",
        ppt: "#fb8c00", pptx: "#fb8c00",
        zip: "#6d4c41", rar: "#6d4c41", "7z": "#6d4c41",
        txt: "#546e7a",
        mp4: "#8e24aa", webm: "#8e24aa", mov: "#8e24aa",
        mp3: "#00897b", wav: "#00897b",
      };
      return {
        backgroundColor: palette[ext] || "#607d8b",
      };
    },
    openPreview(item) {
      const url = this.resolveFileUrl(item.path);
      if (this.isImage(item.original_name)) {
        this.previewItem = {
          src: url,
          name: item.name,
          original_name: item.original_name,
        };
        this.previewDialog = true;
      } else if (this.isViewable(item.original_name)) {
        window.open(url, "_blank", "noopener");
      }
    },
    openPendingPreview(pending) {
      if (this.isImage(pending.file.name) && pending.previewUrl) {
        this.previewItem = {
          src: pending.previewUrl,
          name: pending.name,
          original_name: pending.file.name,
        };
        this.previewDialog = true;
      } else if (this.isViewable(pending.file.name)) {
        const url = URL.createObjectURL(pending.file);
        window.open(url, "_blank", "noopener");
      }
    },
    totalFiles(doc) {
      return (doc.paths?.length || 0) + (doc._pending?.length || 0);
    },
    startEdit(key) {
      this.editingKey = key;
    },
    stopEdit() {
      this.editingKey = null;
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },
    resolveFileUrl(path) {
      if (!path) return "#";
      if (/^https?:\/\//i.test(path)) return path;
      return `${this.apiOrigin}/${path.replace(/^\/+/, "")}`;
    },
    goBack() {
      this.$router.push("/khachhang");
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "0 ₫";
      return new Intl.NumberFormat("vi-VN", {
        style: "currency",
        currency: "VND",
      }).format(value);
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("vi-VN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.customer-detail {
  max-width: 1200px;
  margin: 0 auto;
}

.finance-summary {
  background: linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%);
  border: 1px solid #e8ecf1;
}

.document-panels >>> .v-expansion-panel {
  border: 1px solid #e0e0e0 !important;
  background: #fafbfc !important;
  transition: all 0.2s ease;
}

.document-panels >>> .v-expansion-panel:hover {
  border-color: #b39ddb !important;
  box-shadow: 0 2px 8px rgba(103, 58, 183, 0.08);
}

.document-panels >>> .v-expansion-panel--active {
  border-color: #7e57c2 !important;
  background: #ffffff !important;
}

.document-panels >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.file-row {
  background: #f7f8fa;
  border: 1px solid #eceff1;
  transition: all 0.15s ease;
}

.file-row:hover {
  background: #f0f2f5;
  border-color: #cfd8dc;
}

.pending-row {
  background: #fff8e1;
  border-color: #ffe082;
}

.pending-row:hover {
  background: #fff3cd;
}

.file-name-display {
  cursor: text;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px dashed transparent;
  transition: all 0.15s ease;
  max-width: 100%;
  min-height: 28px;
}

.file-name-display:hover {
  background: rgba(103, 58, 183, 0.06);
  border-color: #d1c4e9;
}

.file-name-display .edit-icon {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.file-name-display:hover .edit-icon {
  opacity: 1;
}

.file-name-input-edit >>> input {
  font-size: 0.875rem;
  font-weight: 500;
}

.file-name-input-edit >>> .v-input__slot {
  min-height: 32px !important;
  padding: 0 8px !important;
}

.file-name-input-edit >>> fieldset {
  border-color: #7e57c2 !important;
}

.dropzone {
  border: 2px dashed #d1c4e9;
  border-radius: 8px;
  background: #faf7ff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropzone:hover {
  border-color: #9575cd;
  background: #f3edff;
}

.dropzone-active {
  border-color: #673ab7;
  background: #ede7f6;
  transform: scale(1.01);
}

.empty-state {
  background: #fafafa;
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
}

.file-thumb {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #eceff1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
}

.file-thumb.clickable {
  cursor: pointer;
}

.file-thumb.clickable:hover {
  transform: scale(1.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ext-chip {
  display: inline-block;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 3px;
  line-height: 1.4;
  margin-right: 2px;
}

.thumb-ext {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 0 2px;
  text-align: center;
  line-height: 1;
}

.preview-wrapper {
  background: #1e1e1e;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  background: rgba(0, 0, 0, 0.6);
}

.preview-body {
  background: #000;
  max-height: 80vh;
  overflow: auto;
}

.preview-img {
  max-width: 100%;
  max-height: 80vh;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

@media only screen and (max-width: 600px) {
  .customer-detail {
    padding: 8px !important;
  }
}
</style>
